import { ApiError } from "../api/client";
import { formatClock } from "../ui/format";
import { humanise } from "../viewer/math";

/**
 * Human copy for every `error_code` in docs/07 §3. Errors are sentences
 * (docs/08 §4): never show a code, a path or Blender text. The map is keyed by
 * the literal code so a missing entry is a type error, not a blank screen.
 */

export const ERROR_CODES = [
  "unauthenticated",
  "forbidden",
  "not_found",
  "invalid_options",
  "asset_too_large",
  "asset_not_image",
  "job_limit",
  "storage_full",
  "restitch_limit",
  "frames_expired",
  "already_rendered",
  "invalid_audio",
  "validation_error",
  "rate_limited",
  "insufficient_credits",
  "billing_suspended",
  "billing_unavailable",
  "invalid_state",
  "not_available",
  "idempotency_conflict",
  "url_unreachable",
  "generation_failed",
  "generation_refused",
  "render_failed",
  "capacity_unavailable",
  "assetlib_unusable",
  "assetlib_full",
  "skin_generation_unavailable",
  "design_limit",
  "mesh_generation_unavailable",
  "cancelled",
  "lease_lost",
  "gpu_class_mismatch",
  "script_hash_mismatch",
  // docs/SPEC.md §25.6
  "brief_pending",
  // 2026-10-18 fix(brief) card: a change of the brief card the rules refuse
  "brief_does_not_fit",
  // docs/SPEC.md §6.3, §6.4 (P28-T12, P28-T13): a cast row the film no longer
  // has; a design whose ad is gone
  "cast_item_gone",
  "no_ad_thread",
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export const ERROR_COPY: Record<ErrorCode, string> = {
  unauthenticated: "Please sign in to continue.",
  forbidden: "You do not have access to this.",
  not_found: "We could not find that.",
  invalid_options:
    "One of the options is not available for this template. Pick another value and try again.",
  asset_too_large: "That image is too large. Please use a smaller file.",
  asset_not_image:
    "That file is not an image we can use. Please upload a PNG, JPEG or WebP.",
  job_limit:
    "You have reached the limit of jobs for now. Wait for one to finish, or try again a little later.",
  storage_full:
    "Your storage is full. Delete some uploads, videos or 3D objects to make room, then try again.",
  restitch_limit:
    "You have re-stitched this job as many times as allowed today. Please try again tomorrow.",
  frames_expired:
    "The frames for this render are no longer stored, so this edit needs a new render.",
  already_rendered:
    "This ad has already been rendered at that aspect and size. Pick another combination.",
  invalid_audio:
    "The soundtrack could not be previewed; one of its cues is not valid.",
  validation_error: "One of the fields is not valid. Check it and try again.",
  rate_limited:
    "Too many requests in a short time. Please wait a moment and try again.",
  insufficient_credits:
    "You do not have enough credits for this. Top up in Intelibo Accounts and try again.",
  billing_suspended:
    "Billing on your account is suspended. Please resolve it in Intelibo Accounts.",
  billing_unavailable:
    "We could not reach billing just now. Nothing was charged; please try again shortly.",
  invalid_state:
    "That action is not available while the job is in its current state.",
  not_available: "This feature is not available yet.",
  idempotency_conflict:
    "This request was already sent with different details. Please refresh and try again.",
  url_unreachable:
    "We could not reach the website you linked. Check the address, or remove it and try again.",
  generation_failed:
    "We could not build a scene from this brief. Nothing was charged; try rephrasing it.",
  generation_refused:
    "We cannot produce this ad as described. Nothing was charged; please adjust the brief.",
  render_failed: "The scene could not be rendered. Nothing was charged.",
  capacity_unavailable:
    "No render capacity is available right now. Nothing was charged; please try again soon.",
  assetlib_unusable:
    "A 3D object for this ad could not be loaded. Nothing was charged; please try again.",
  assetlib_full: "Objects is full ({limit} items). Delete something first.",
  skin_generation_unavailable: "Generated artwork is not available yet.",
  design_limit:
    "You have {limit} designs, the most allowed. Delete one to start another.",
  mesh_generation_unavailable: "Sculpted designs are not available yet.",
  cancelled:
    "This job was cancelled. You were charged only for the frames that were rendered.",
  lease_lost:
    "A render worker lost its claim on this work; it will be picked up again.",
  gpu_class_mismatch:
    "A render worker was not the right kind of machine for this work.",
  script_hash_mismatch:
    "A render worker received a script that did not match this job.",
  brief_pending:
    "TrueReel is still answering. Wait for it, then say the next thing.",
  // the same sentence as backend/ads/brief_card.py::CARD_SENTENCES
  brief_does_not_fit:
    "That change does not fit the rest of the brief, so the brief was left as it was. Try it another way, or say what you want in the conversation.",
  cast_item_gone:
    "That object is no longer in the film, so nothing was changed. The list of objects shows the film as it is now.",
  no_ad_thread:
    "This design is no longer being made for an ad (the ad may have been deleted). Your design stays here in the design studio.",
};

export const FALLBACK_MESSAGE = "Something went wrong. Please try again.";

export type Params = Record<string, unknown>;

/** Replaces every `{key}` in a sentence with its parameter; numbers are
 *  written with `toLocaleString("en-US")`. A key with no parameter stays. */
export function fill(sentence: string, params?: Params | null): string {
  if (!params) return sentence;
  return sentence.replace(/\{(\w+)\}/g, (whole, key: string) => {
    if (!Object.prototype.hasOwnProperty.call(params, key)) return whole;
    const value = params[key];
    if (typeof value === "number") return value.toLocaleString("en-US");
    return value === null || value === undefined ? whole : String(value);
  });
}

/** Copy for an HTTP error code; `params` fills `{limit}` and the like.
 *  Unknown codes get a safe generic sentence. */
export function messageFor(
  code: string | null | undefined,
  params?: Params | null,
): string {
  if (code && Object.prototype.hasOwnProperty.call(ERROR_COPY, code)) {
    return fill(ERROR_COPY[code as ErrorCode], params);
  }
  return FALLBACK_MESSAGE;
}

/** What the page was doing when a request failed ([ux §5.6]). `still` and
 *  `plain` have no sentence of their own: they make the rules for limits and
 *  fields apply. */
export type ErrorContext =
  | "design"
  | "sculpted"
  | "asset_delete"
  | "import"
  | "instruction"
  | "generate"
  | "colour"
  | "remix"
  | "still"
  | "plain";

/** The sentences an existing code takes from its context ([ux §5.4]). */
export const CONTEXT_COPY = {
  design_busy: "This design is busy. Wait for it to finish.",
  sculpted: "Sculpted designs cannot be tuned or given other surfaces.",
  asset_delete:
    "This is the default surface of {names}. Change or delete those objects first.",
  import: "This model is being checked. Wait for it to finish.",
  // §15.1's block, the same sentence as every other action's (P28-T08)
  instruction: "This needs about {needed} credits; you have {available}.",
  generate: "This needs about {needed} credits; you have {available}.",
  daily_credits:
    "Your organisation has reached today's credit limit. Try again tomorrow.",
  colour: "Use a colour like #4d2c91.",
  fit: "This picture is too far from the area's shape.",
  slot: "{label}: that choice does not fit this surface.",
  remix: "This object can be dressed but not reshaped.",
  publish: "The model could not be read.",
} as const;

/** One sentence per rate limit that has its own. `{limit}` is the limit's
 *  value (`detail.max`), `{clock}` the time it opens again. */
export const RATE_LIMIT_COPY: Record<string, string> = {
  stills_hour:
    "You have made {limit} true renders this hour. Try again at {clock}.",
  stills_day: "You have made {limit} true renders today. Try again tomorrow.",
  mesh_day: "You have used today's sculpted designs. Try again tomorrow.",
  turns_day: "You have sent today's limit of instructions. Try again tomorrow.",
  param_builds_hour:
    "That is a lot of tuning for one hour. Try again at {clock}.",
  imports_day: "You have imported {limit} models today. Try again tomorrow.",
  ingests_hour: "That is a lot for one hour. Try again at {clock}.",
  asset_writes_hour: "That is a lot for one hour. Try again at {clock}.",
  asset_detail_hour: "That is a lot for one hour. Try again at {clock}.",
};

/** `invalid_state` by its `detail.reason`: the sentence and the contexts it
 *  is said in; `null` is any context. */
const REASON_COPY: Record<
  string,
  { sentence: string; contexts: readonly ErrorContext[] | null }
> = {
  request_pending: {
    sentence: "Wait for the change in progress to finish.",
    contexts: null,
  },
};

/** The sentence of an `invalid_state` by its reason, or null when the
 *  reason has none in this context. */
export function reasonSentence(
  reason: unknown,
  context?: ErrorContext,
): string | null {
  if (typeof reason !== "string" || !Object.hasOwn(REASON_COPY, reason)) {
    return null;
  }
  const row = REASON_COPY[reason];
  if (row.contexts === null) return row.sentence;
  return context !== undefined && row.contexts.includes(context)
    ? row.sentence
    : null;
}

function detailOf(error: ApiError): Params {
  return error.detail && typeof error.detail === "object"
    ? (error.detail as Params)
    : {};
}

/** The sentence for a failed request, with the context rules of [ux §5.4].
 *  The server's own message is never shown. */
export function sentenceFor(error: unknown, context?: ErrorContext): string {
  if (!(error instanceof ApiError)) return FALLBACK_MESSAGE;
  const detail = detailOf(error);
  const code = error.code;

  if (code === "invalid_state") {
    const sentence = reasonSentence(detail.reason, context);
    if (sentence !== null) return sentence;
  }

  if (code === "rate_limited") {
    const name = typeof detail.limit === "string" ? detail.limit : "";
    if (Object.prototype.hasOwnProperty.call(RATE_LIMIT_COPY, name)) {
      const wait =
        typeof detail.retry_after === "number" ? detail.retry_after : 0;
      return fill(RATE_LIMIT_COPY[name], {
        limit: detail.max,
        clock: formatClock(Date.now() + wait * 1000),
      });
    }
  }

  if (code === "validation_error") {
    const field = typeof detail.field === "string" ? detail.field : "";
    if (field === "fit") return CONTEXT_COPY.fit;
    if (field.startsWith("slot:")) {
      return fill(CONTEXT_COPY.slot, { label: humanise(field.slice(5)) });
    }
    if (field === "publish") return CONTEXT_COPY.publish;
    if (context === "colour") return CONTEXT_COPY.colour;
    if (context === "remix") return CONTEXT_COPY.remix;
    return ERROR_COPY.validation_error;
  }

  if (code === "invalid_state") {
    if (
      context === "design" ||
      context === "instruction" ||
      context === "generate"
    ) {
      return CONTEXT_COPY.design_busy;
    }
    if (context === "sculpted") return CONTEXT_COPY.sculpted;
    if (context === "import") return CONTEXT_COPY.import;
    if (context === "asset_delete") {
      const dependents = Array.isArray(detail.dependents)
        ? (detail.dependents as { name?: unknown }[])
        : [];
      const names = dependents
        .map((d) => (typeof d?.name === "string" ? d.name : ""))
        .filter(Boolean)
        .join(", ");
      return fill(CONTEXT_COPY.asset_delete, { names });
    }
  }

  if (code === "insufficient_credits") {
    if (context === "instruction") {
      return fill(CONTEXT_COPY.instruction, {
        needed: detail.needed,
        available: detail.available,
      });
    }
    if (context === "generate") {
      return fill(CONTEXT_COPY.generate, {
        needed: detail.needed,
        available: detail.available,
      });
    }
  }

  if (code === "job_limit" && detail.limit === "daily_credits") {
    return CONTEXT_COPY.daily_credits;
  }

  return messageFor(code, detail);
}

/** A parameter that is a name the server already reduced to plain characters. */
function text(value: unknown): string {
  return typeof value === "string" || typeof value === "number"
    ? String(value)
    : "";
}

/** A part of a model as its row is labelled: `m1` reads "Surface 1". */
function surface(p: Params): string {
  return typeof p.slot === "string" && p.slot
    ? humanise(p.slot)
    : "one surface";
}

const UNREADABLE = "The model could not be read.";

/** "1 credit", "1,204 credits". */
export function creditsWord(credits: number): string {
  return `${credits.toLocaleString("en-US")} ${credits === 1 ? "credit" : "credits"}`;
}

/**
 * Codes that arrive on a row (`error_code` + `error_params` of a model being
 * brought in, of a true render of an object, of a step of a design) rather than as a failed request
 * ([ux §5.5]). Keyed by the literal code, so a missing sentence is a type
 * error.
 */
export const ROW_ERROR_CODES = [
  "ingest_unreadable",
  "ingest_timeout",
  "ingest_n1",
  "ingest_n2",
  "ingest_n3",
  "ingest_n4",
  "ingest_n5",
  "ingest_n6",
  "ingest_n7",
  "ingest_n8",
  "glb_not_glb",
  "glb_version",
  "glb_damaged",
  "glb_external",
  "glb_extension",
  "glb_triangles",
  "glb_images",
  "zip_unsafe",
  "zip_too_many_files",
  "zip_too_large",
  "zip_no_model",
  "convert_failed",
  "convert_too_large",
  "interrupted",
  "ingest_overdue",
  "still_timeout",
  "still_failed",
  "no_credits",
  "model_turn_failed",
  "model_service_unavailable",
  "model_turn_timeout",
  "model_refused",
  "model_build_failed",
  "model_build_timeout",
  "publish_failed",
  "mesh_generation_failed",
  "mesh_multi_surface",
] as const;

export type RowErrorCode = (typeof ROW_ERROR_CODES)[number];

export const ROW_ERROR_COPY: Record<RowErrorCode, (p: Params) => string> = {
  ingest_unreadable: () => UNREADABLE,
  ingest_timeout: (p) =>
    fill(
      "The model took too long to process (over {minutes} minutes). Try a simpler file.",
      p,
    ),
  ingest_n1: () => UNREADABLE,
  ingest_n2: () => UNREADABLE,
  ingest_n3: () => UNREADABLE,
  ingest_n4: () => UNREADABLE,
  ingest_n5: (p) =>
    fill(
      "The model has {materials} materials; TrueReel reads at most {limit}. Merge materials in your 3D tool and export again.",
      p,
    ),
  ingest_n6: (p) =>
    `The texture layout of ${surface(p)} could not be used. Press Fix stretching on that surface, or let ads change its material.`,
  ingest_n7: (p) => `The label or screen area on ${surface(p)} is too small.`,
  ingest_n8: (p) =>
    fill(
      "The model is too detailed ({triangles} triangles; the limit is {limit}).",
      p,
    ),
  glb_not_glb: () => "This is not a .glb file.",
  glb_version: () => "Only glTF 2.0 files are accepted.",
  glb_damaged: () => "The file is incomplete or damaged.",
  glb_external: () =>
    "The file refers to other files; export it as one self-contained .glb.",
  glb_extension: (p) =>
    `The file needs a glTF feature TrueReel does not read: ${text(p.name)}.`,
  glb_triangles: (p) =>
    fill("The model has {triangles} triangles; the limit is {limit}.", p),
  glb_images: () =>
    "The pictures inside the file cannot be used (too many, too large, or an unsupported type).",
  zip_unsafe: () => "The archive is not safe to unpack.",
  zip_too_many_files: (p) =>
    fill("The archive has too many files (limit {limit}).", p),
  zip_too_large: (p) =>
    fill("The archive is too large when unpacked (limit {limit_gb} GB).", p),
  zip_no_model: () => "The archive holds no model file.",
  convert_failed: () => "The model file could not be opened.",
  convert_too_large: () => "The converted model is too large.",
  interrupted: () =>
    "This was interrupted on our side. Nothing was charged. Try again.",
  ingest_overdue: (p) =>
    fill(
      "The check did not finish within {minutes} minutes because of a problem on our side. Nothing was charged. Try again.",
      p,
    ),
  // A true render of an object ([ux §5.5]): the page passes the still's
  // `size`. It is free (§15.1, a CPU still).
  still_timeout: (p) =>
    [
      "This one is taking too long to render.",
      p.size === 1024 ? "Try the smaller size." : "",
    ]
      .filter(Boolean)
      .join(" "),
  still_failed: () => "The picture could not be rendered. Nothing was charged.",
  no_credits: () =>
    "There were not enough credits to start. Nothing was charged.",
  // A step of a design ([design-studio §7.9]); none carries parameters. A
  // refusal's reason is shown by the page, under this sentence.
  model_turn_failed: () =>
    "The design could not be completed. Nothing was charged. You can try again.",
  model_service_unavailable: () =>
    "A problem on our side stopped the design. Nothing was charged. Please try again later.",
  model_turn_timeout: () =>
    "This took too long and was stopped. Nothing was charged.",
  model_refused: () =>
    "We cannot model this as described. Nothing was charged; please change the description.",
  model_build_failed: () =>
    "That could not be built. Your design is unchanged.",
  model_build_timeout: () =>
    "That took too long to build. Your design is unchanged.",
  // Adding a design to Objects; `interrupted` reads this one too.
  publish_failed: () => "It could not be added. Nothing was charged.",
  // A sculpted step ([sculpted §3.4]): generated in one piece by a provider.
  mesh_generation_failed: () =>
    "The sculpted model could not be generated. Nothing was charged.",
  mesh_multi_surface: () =>
    "The generated model has more than one surface; try again. Nothing was charged.",
};

/** The sentence for a row's `error_code`, filled from its `error_params`. */
export function rowMessageFor(
  code: string | null | undefined,
  params?: Params | null,
): string {
  if (code && Object.prototype.hasOwnProperty.call(ROW_ERROR_COPY, code)) {
    return ROW_ERROR_COPY[code as RowErrorCode](params ?? {});
  }
  return FALLBACK_MESSAGE;
}

/** What a check of a model noted without failing ([ux §6]). */
export const WARNING_CODES = [
  "decimated",
  "texture_missing",
  "set_real_height",
  "archive_several_models",
  "archive_dropped",
  "textures_not_in_archive",
  "uv_overlap",
  "thumb_fallback",
] as const;

export type WarningCode = (typeof WARNING_CODES)[number];

export const WARNING_COPY: Record<WarningCode, (p: Params) => string | null> = {
  decimated: (p) =>
    fill(
      "The model was simplified from {source_triangles} to {triangles} triangles to render quickly.",
      p,
    ),
  texture_missing: (p) =>
    `A texture of ${text(p.name)} is missing from the file.`,
  set_real_height: () => "Set the real height: the file's scale looks wrong.",
  archive_several_models: (p) =>
    `The archive had several models; we used ${text(p.used)}.`,
  archive_dropped: (p) => fill("{count} files in the archive were ignored.", p),
  textures_not_in_archive: (p) =>
    `Some textures the model asks for were not in the archive: ${(Array.isArray(
      p.names,
    )
      ? p.names
      : []
    )
      .map(text)
      .filter(Boolean)
      .join(", ")}. Upload a .zip that includes them, or continue without.`,
  uv_overlap: () => "This surface's texture layout overlaps itself.",
  // The picture of the model fell back to a plainer one: nothing to say.
  thumb_fallback: () => null,
};

/** The sentence of one warning; null for one that is not shown, and for a
 *  code this page does not know. */
export function warningFor(warning: {
  code: string;
  params?: Params | null;
}): string | null {
  if (Object.prototype.hasOwnProperty.call(WARNING_COPY, warning.code)) {
    return WARNING_COPY[warning.code as WarningCode](warning.params ?? {});
  }
  return null;
}
